import { useEffect, useState, useRef, useCallback } from 'react';

type Props = {
  targetDate: Date;
  onExpire?: () => void;
  className?: string;
};

/** Re-fetch while past start until API reflects LIVE (cron may lag a few seconds). */
const EXPIRED_REFETCH_MS = 4000;

export function CountdownTimer({ targetDate, onExpire, className = '' }: Props) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const lastExpireEmitRef = useRef(0);

  const tick = useCallback(() => {
    const now = Date.now();
    const distance = targetDate.getTime() - now;
    const urgent = distance > 0 && distance < 300000;
    setIsUrgent(Boolean(urgent));

    if (distance < 0) {
      setTimeLeft('00:00:00');
      if (onExpire && now - lastExpireEmitRef.current >= EXPIRED_REFETCH_MS) {
        lastExpireEmitRef.current = now;
        onExpire();
      }
      return;
    }

    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    setTimeLeft(
      `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    );
  }, [targetDate, onExpire]);

  useEffect(() => {
    lastExpireEmitRef.current = 0;
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tick]);

  return (
    <span
      className={`inline-block rounded-md px-2 py-1 text-xs font-black uppercase tracking-wider tabular-nums ${
        isUrgent ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-blue-100 text-blue-600'
      } ${className}`}
    >
      {timeLeft}
    </span>
  );
}
